import { useCallback, useEffect, useState } from 'react';

import { useSoyuzData } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { Mediator } from '@spotahome/soyuz-mediator';

import { useCookie } from '../utils/withCookie';
import { CHANNELS } from '../utils/constants';
import { useMediaQuery } from '../utils/breakpoints/breakpoints';

import './ZohoChatbot.scss';

const SCRIPT_ID = 'zoho-chat-script';
const TWO_WEEKS = 60 * 60 * 24 * 14; // 2 weeks in seconds

const ZohoChatbot = () => {
  const [isZohoChatLoaded, setIsZohoChatLoaded] = useState(false);
  const [showZohoChat, setShowZohoChat] = useState(true);
  const { isBiggerThanTablet } = useMediaQuery();
  const { getCookie, setCookie } = useCookie();
  const soyuzData = useSoyuzData();

  const pageName = Object.keys(soyuzData)[0] || 'unknown';

  const hasUsedSpottyCat = getCookie('SpottyCatUsed') !== undefined;

  const loadScripts = useCallback(() => {
    const scriptAlreadyInjected = document.querySelector(`#${SCRIPT_ID}`);

    if (scriptAlreadyInjected) {
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.defer = true;
    script.type = 'text/javascript';
    script.innerHTML =
      'window.$zoho=window.$zoho || {};$zoho.salesiq=$zoho.salesiq||{ready:function(){}}';
    document.body.appendChild(script);

    const scriptSrc = document.createElement('script');
    scriptSrc.defer = true;
    scriptSrc.type = 'text/javascript';
    scriptSrc.src =
      '//salesiq.zohopublic.com/widget?wc=siq05454a5f6626ff134ffe8042ca1fbb7f2505ed7276493711f51ad5ac9d30f99d';
    scriptSrc.onload = () => {
      setIsZohoChatLoaded(true);
    };
    document.body.appendChild(scriptSrc);
  }, []);

  const addAlreadyUsedClass = () => {
    const zohoChatElement = document.querySelector('#zsiq_float');
    if (zohoChatElement) {
      zohoChatElement.classList.add('zsiq-custom-used');
    }
  };

  useEffect(() => {
    const withChatInMobile = fn => () => {
      if (isBiggerThanTablet() && window.$zoho?.salesiq?.floatbutton) {
        window.$zoho?.salesiq.floatbutton.visible('show');
        setShowZohoChat(true);
        return;
      }

      fn();
    };

    const hideButton = withChatInMobile(() => {
      if (window.$zoho?.salesiq?.floatbutton) {
        window.$zoho.salesiq.floatbutton.visible('hide');
      }
      setShowZohoChat(false);
    });

    const showButton = withChatInMobile(() => {
      if (window.$zoho?.salesiq?.floatbutton) {
        window.$zoho.salesiq.floatbutton.visible('show');
      }
      setShowZohoChat(true);
    });

    Mediator.subscribe(CHANNELS.HOMECARD_MAP_OPEN, hideButton);
    Mediator.subscribe(CHANNELS.HOMECARD_MAP_CLOSE, showButton);
    Mediator.subscribe(CHANNELS.OPEN_EXPOSED_FILTER, hideButton);
    Mediator.subscribe(CHANNELS.CLOSE_EXPOSED_FILTER, showButton);
    Mediator.subscribe(CHANNELS.GOOGLE_ONE_TAP_SHOWN, hideButton);
    Mediator.subscribe(CHANNELS.GOOGLE_ONE_TAP_HIDDEN, showButton);

    return () => {
      Mediator.unsubscribe(CHANNELS.HOMECARD_MAP_OPEN, hideButton);
      Mediator.unsubscribe(CHANNELS.HOMECARD_MAP_CLOSE, showButton);
      Mediator.unsubscribe(CHANNELS.OPEN_EXPOSED_FILTER, hideButton);
      Mediator.unsubscribe(CHANNELS.CLOSE_EXPOSED_FILTER, showButton);
      Mediator.unsubscribe(CHANNELS.GOOGLE_ONE_TAP_SHOWN, hideButton);
      Mediator.unsubscribe(CHANNELS.GOOGLE_ONE_TAP_HIDDEN, showButton);
    };
  }, [showZohoChat, isBiggerThanTablet]);

  useEffect(() => {
    if (!isZohoChatLoaded) {
      loadScripts();
    }
  }, [isZohoChatLoaded, loadScripts]);

  useEffect(() => {
    if (!isZohoChatLoaded) {
      return;
    }

    window.$zoho.salesiq.afterReady = () => {
      window.$zoho.salesiq.chat.online(() => {
        if (hasUsedSpottyCat) {
          addAlreadyUsedClass();
        }
      });

      window.$zoho.salesiq.chatbutton.click(() => {
        setCookie('SpottyCatUsed', 1, { maxAge: TWO_WEEKS });
        addAlreadyUsedClass();
        SoyuzAnalytics.sendGA4Event('send', {
          context: 'ZohoChat',
          action: 'Open',
          value: 'click',
          content_type: pageName
        });
      });

      window.$zoho.salesiq.floatwindow.close(() => {
        SoyuzAnalytics.sendGA4Event('send', {
          context: 'ZohoChat',
          action: 'Close',
          value: 'click',
          content_type: pageName
        });
      });

      if (!showZohoChat) {
        window.$zoho.salesiq.floatbutton.visible('hide');
      }
    };
  }, [showZohoChat, isZohoChatLoaded, hasUsedSpottyCat, setCookie]);

  return null;
};

export default ZohoChatbot;
